import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Copy, KeyRound, Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { Panel, Tag, Initials } from "@/components/kit";
import { ConfirmDialog, DIALOG_CLS } from "@/components/Dialogs";
import { useStore } from "@/store/AppStore";
import { teamOf } from "@/lib/selectors";
import { ACCESS_CATEGORIES } from "@/data/mock";

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error("Não foi possível copiar neste navegador.");
  }
};

function AccessForm({ initial, onSave }) {
  const [f, setF] = useState({ category: "Wi-Fi", name: "", login: "", password: "", url: "", note: "", ...initial });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Dê um nome ao acesso.");
    onSave(f);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="access-form">
      <Field label="Tipo">
        <Select value={f.category} onValueChange={set("category")}>
          <SelectTrigger data-testid="access-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{ACCESS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Nome / serviço"><Input value={f.name} onChange={set("name")} placeholder="Ex.: Wi-Fi da recepção" data-testid="access-name-input" /></Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Login / usuário"><Input value={f.login} onChange={set("login")} data-testid="access-login-input" /></Field>
        <Field label="Senha / código"><Input value={f.password} onChange={set("password")} data-testid="access-password-input" /></Field>
      </div>
      <Field label="Link (opcional)"><Input value={f.url} onChange={set("url")} placeholder="https://" data-testid="access-url-input" /></Field>
      <Field label="Observação"><Textarea rows={2} value={f.note} onChange={set("note")} data-testid="access-note-input" /></Field>
      <Button type="submit" className="w-full font-semibold" data-testid="access-save-button">Salvar acesso</Button>
    </form>
  );
}

function Secret({ a }) {
  const [show, setShow] = useState(false);
  if (!a.password) return <span className="text-neutral-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-sm" data-testid={`access-password-${a.id}`}>{show ? a.password : "••••••••"}</span>
      <button aria-label="Mostrar senha" onClick={() => setShow(!show)} data-testid={`access-toggle-${a.id}`} className="rounded p-1.5 text-neutral-400 hover:text-gold-dark">{show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
      <button aria-label="Copiar senha" onClick={() => copy(a.password, "Senha copiada.")} data-testid={`access-copy-${a.id}`} className="rounded p-1.5 text-neutral-400 hover:text-gold-dark"><Copy className="h-3.5 w-3.5" /></button>
    </span>
  );
}

function AccessRow({ a, onEdit, onDelete }) {
  return (
    <li data-testid={`access-${a.id}`} className="grid grid-cols-1 gap-2 py-4 sm:grid-cols-[1.6fr_1.3fr_1.2fr_auto] sm:items-center">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-semibold">{a.name} {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="text-gold-dark" data-testid={`access-link-${a.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>}</p>
        <div className="mt-1"><Tag>{a.category}</Tag></div>
        {a.note && <p className="mt-1 text-xs text-neutral-500">{a.note}</p>}
      </div>
      <p className="truncate text-sm text-neutral-700" data-testid={`access-login-${a.id}`}><span className="text-xs text-neutral-400 sm:hidden">Login: </span>{a.login || "—"}</p>
      <Secret a={a} />
      {onEdit ? (
        <span className="flex">
          <button aria-label="Editar" onClick={onEdit} data-testid={`access-edit-${a.id}`} className="rounded p-2 text-neutral-400 hover:text-gold-dark"><Pencil className="h-4 w-4" /></button>
          <button aria-label="Excluir" onClick={onDelete} data-testid={`access-delete-${a.id}`} className="rounded p-2 text-neutral-400 hover:text-gold-dark"><Trash2 className="h-4 w-4" /></button>
        </span>
      ) : <span className="hidden w-16 sm:block" />}
    </li>
  );
}

export function AccessSheet({ companyId, editable }) {
  const { state, saveAccess, deleteAccess } = useStore();
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const close = (v) => !v && setDlg(null);
  const list = state.accesses.filter((a) => a.companyId === companyId && `${a.name} ${a.category} ${a.login}`.toLowerCase().includes(q.toLowerCase()));
  const save = (data) => { saveAccess({ ...data, companyId }); toast.success(data.id ? "Acesso atualizado." : "Acesso cadastrado."); setDlg(null); };
  return (
    <Panel title="Planilha de acessos" subtitle="Wi-Fi, computadores, sistemas, alarmes e demais logins da empresa" testId="access-sheet" action={editable && <Button size="sm" onClick={() => setDlg({ type: "edit" })} data-testid="access-new-button"><Plus className="mr-1 h-4 w-4" /> Novo acesso</Button>}>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar acesso" className="pl-9" data-testid="access-search-input" />
      </div>
      <div className="hidden grid-cols-[1.6fr_1.3fr_1.2fr_auto] gap-2 border-b border-neutral-100 py-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400 sm:grid"><span>Serviço</span><span>Login</span><span>Senha</span><span className="w-16" /></div>
      {!list.length && <p className="py-6 text-sm text-neutral-400">Nenhum acesso encontrado.</p>}
      <ul className="divide-y divide-neutral-100">
        {list.map((a) => <AccessRow key={a.id} a={a} onEdit={editable && (() => setDlg({ type: "edit", a }))} onDelete={() => setDlg({ type: "del", a })} />)}
      </ul>
      <Dialog open={dlg?.type === "edit"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid="access-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{dlg?.a ? "Editar acesso" : "Novo acesso"}</DialogTitle></DialogHeader>
          {dlg?.type === "edit" && <AccessForm initial={dlg.a} onSave={save} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={close} title="Excluir acesso?" description={dlg?.a?.name} testId="access-delete-dialog" onConfirm={() => { deleteAccess(dlg.a.id); toast.success("Acesso excluído."); }} />
    </Panel>
  );
}

export function AccessPermissions({ companyId }) {
  const { state, updateCollaborator } = useStore();
  return (
    <Panel title="Colaboradoras autorizadas" subtitle="Somente quem estiver autorizada vê a planilha (sem poder editar)" testId="access-permissions">
      <ul className="divide-y divide-neutral-100">
        {teamOf(state, companyId).map((c) => (
          <li key={c.id} className="flex items-center gap-3 py-3">
            <Initials name={c.name} />
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{c.name}</p><p className="text-xs text-neutral-500">{c.role}</p></div>
            <Switch checked={!!c.accessAllowed} onCheckedChange={(v) => { updateCollaborator(c.id, { accessAllowed: v }); toast.success(v ? `${c.name.split(" ")[0]} agora vê os acessos.` : "Acesso removido."); }} data-testid={`access-allow-${c.id}`} />
          </li>
        ))}
      </ul>
      <p className="mt-4 flex items-center gap-2 text-xs text-neutral-500"><KeyRound className="h-3.5 w-3.5 text-gold" /> A consultora Master não tem acesso às senhas.</p>
    </Panel>
  );
}
